import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Col, Input, Popconfirm, Row, Switch, message, Select } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import modal from '~components/modal';
import { dayFormatType, formatDay } from '~helpers/day';
import AsyncCountrySelector from './asyncCountrySelector';
import TiersGroup from './tiersGroup';

@modal(
  {
    className: 'commission-plan-editor-modal',
  },
  true,
)
class CommissionPlanEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      comissionPlanName: props.plan?.name || '',
      externalName: props.plan?.externalName || '',
      countryCode: props.plan?.countryCode || undefined,
      currency: props.plan?.currency || '',
      calculationType: props.plan?.tiers?.[0]?.calculationRules[0]?.calculationType || undefined,
      partnerCommissionCalculation: props.plan?.country.partnerCommissionCalculation || null,
      defaultPlan: props.plan?.default || false,
      tiers:
        props.plan?.tiers?.map(({ calculationRules, __typename, ...tier }) => {
          //unuse code,just fool lint.
          calculationRules && __typename;
          return tier;
        }) || [],
      defaultPopconfirmVisible: false,
      defaultPopConfirmLabel: null,
      tiersErrInfo: null,
      loading: false,
    };
  }

  handleCheckDefaultPlan = (countryCode, isDefaultPlan) =>
    new Promise(resolve => {
      this.props.handleCheckDefaultPlan(countryCode).then(res => {
        const existDefaultPlan = res.getCountryDefaultReferralCommissionPlan;
        if (isDefaultPlan) {
          if (existDefaultPlan?.id) {
            if (this.props.plan?.id != existDefaultPlan?.id) {
              return resolve(
                this.props.t('cms.referral.commission_plan.modal.error.default_plan.exist', {
                  value: existDefaultPlan?.name,
                }),
              );
            }
          }
        }
        resolve(false);
      });
    });

  /*
    validateFieldGroup?:{
      fieldIndex:number,
      validateFieldKey:string[]
    }
    */
  handleValidateTier = validateFieldGroup => {
    const validateResult = {
      haveNoError: true,
      validateErrorDetail: [],
    };

    const validteSingleFieldGroup = (tierIndex, tier) => {
      const validateSingleFieldErrorDetail = [];
      const existFieldNames = Object.keys(tier);

      if (
        existFieldNames.includes('bookingCountTo') &&
        tierIndex !== this.state.tiers.length - 1 &&
        tier.bookingCountTo == null
      ) {
        validateSingleFieldErrorDetail.push({
          errKey: 'bookingCountTo',
          errIndex: tierIndex,
          errMsg: this.props.t('cms.referral.commission_plan.modal.error.blank'),
        });
      }
      if (
        existFieldNames.includes('shortTermCalculationValue') &&
        tier.shortTermCalculationValue === null
      ) {
        validateSingleFieldErrorDetail.push({
          errKey: 'shortTermCalculationValue',
          errIndex: tierIndex,
          errMsg: this.props.t('cms.referral.commission_plan.modal.error.blank'),
        });
      }
      if (
        existFieldNames.includes('longTermCalculationValue') &&
        tier.longTermCalculationValue === null
      ) {
        validateSingleFieldErrorDetail.push({
          errKey: 'longTermCalculationValue',
          errIndex: tierIndex,
          errMsg: this.props.t('cms.referral.commission_plan.modal.error.blank'),
        });
      }
      if (existFieldNames.includes('name') && !tier.name.trim()) {
        validateSingleFieldErrorDetail.push({
          errKey: 'name',
          errIndex: tierIndex,
          errMsg: this.props.t('cms.referral.commission_plan.modal.error.blank'),
        });
      }
      if (existFieldNames.includes('name') && tier.name?.length > 80) {
        validateSingleFieldErrorDetail.push({
          errKey: 'name',
          errIndex: tierIndex,
          errMsg: this.props.t('cms.referral.commission_plan.modal.error.tier_name.limit'),
        });
      }

      return validateSingleFieldErrorDetail;
    };

    if (validateFieldGroup) {
      const validatingFieldItem = Object.assign(
        {},
        this.state.tiers[validateFieldGroup.fieldIndex],
      );
      const waitCheckAgainKeys =
        this.state.tiersErrInfo?.validateErrorDetail.map(d => d.errKey) || [];
      Object.keys(validatingFieldItem).map(key => {
        if (
          !validateFieldGroup.validateFieldKey.includes(key) &&
          !waitCheckAgainKeys.includes(key)
        ) {
          delete validatingFieldItem[key];
        }
      });
      const result = validteSingleFieldGroup(validateFieldGroup.fieldIndex, validatingFieldItem);
      if (result.length) {
        validateResult.haveNoError = false;
        validateResult.validateErrorDetail = [...validateResult.validateErrorDetail, ...result];
      }
    } else {
      this.state.tiers.map((tier, tierIndex) => {
        const result = validteSingleFieldGroup(tierIndex, tier);
        if (result.length) {
          validateResult.haveNoError = false;
          validateResult.validateErrorDetail = [...validateResult.validateErrorDetail, ...result];
        }
      });
    }

    this.handleUpdateState('tiersErrInfo', validateResult);
    return validateResult;
  };

  handleBeforeSubmitCheck = visible => {
    if (!visible) {
      this.handleUpdateState('defaultPopconfirmVisible', false);
    } else {
      this.setState({ loading: true });
      this.props.form.validateFieldsAndScroll(err => {
        const tierValidateResult = this.handleValidateTier().haveNoError;
        if (!err && tierValidateResult) {
          const params = this.props.form.getFieldsValue();
          this.handleCheckDefaultPlan(params.country, params.default)
            .then(res => {
              if (res) {
                this.handleUpdateState('defaultPopConfirmLabel', res);
                this.handleUpdateState('defaultPopconfirmVisible', true);
              } else {
                this.handleClickConfirm(true);
              }
            })
            .catch(error => {
              console.log(error);
              this.setState({ loading: false });
            });
        } else {
          this.setState({ loading: false });
        }
      });
    }
  };

  handleClickConfirm = continueLoading => {
    const { type } = this.props;
    if (this.state.loading && !continueLoading) {
      return false;
    }

    this.props.form.validateFieldsAndScroll(
      // eslint-disable-next-line consistent-return
      err => {
        const tierValidateResult = this.handleValidateTier().haveNoError;
        if (!err && tierValidateResult) {
          const params = this.props.form.getFieldsValue();
          const transTiers = this.state.tiers.map(tier => ({
            ...tier,
            shortTermCalculationValue: String(tier.shortTermCalculationValue),
            longTermCalculationValue: String(tier.longTermCalculationValue),
            shortTermCalculationType: this.state.calculationType,
            longTermCalculationType: this.state.calculationType,
          }));

          Object.keys(params).map(key => {
            if (typeof params[key] === 'string') {
              params[key] = params[key].trim();
            }
            return true;
          });

          this.setState({ loading: true });

          if (type === 'create') {
            this.props
              .handleSubmit({
                variables: {
                  input: {
                    name: params.comissionPlanName.trim(),
                    externalName: params.externalName,
                    countryCode: params.country,
                    currency: this.state.currency,
                    default: params.default,
                    tiers: transTiers,
                  },
                },
              })
              .then(response => {
                if (response?.data?.createReferralCommissionPlan?.id) {
                  message.success(
                    this.props.t('cms.referral.commission_plan.modal.toast.success', {
                      value: this.props.t(
                        'cms.referral.commission_plan.modal.create_commission_plan.title',
                      ),
                    }),
                  );
                  this.props.handleCloseModal();
                }
              })
              .catch(error => {
                message.error(
                  error?.message
                    ? error.message
                    : this.props.t('cms.referral.commission_plan.modal.error.unknown'),
                  7,
                );
              })
              .finally(() => {
                this.setState({ loading: false });
              });
          }

          if (type === 'edit') {
            this.props
              .handleSubmit({
                variables: {
                  input: {
                    id: this.props.plan?.id,
                    name: params.comissionPlanName.trim(),
                    externalName: params.externalName,
                    countryCode: params.country,
                    currency: this.state.currency,
                    default: params.default,
                    tiers: transTiers,
                  },
                },
              })
              .then(response => {
                if (response?.data?.updateReferralCommissionPlan?.id) {
                  message.success(
                    this.props.t('cms.referral.commission_plan.modal.toast.success', {
                      value: this.props.t(
                        'cms.referral.commission_plan.modal.edit_commission_plan.title',
                      ),
                    }),
                  );
                  this.props.handleCloseModal();
                }
              })
              .catch(error => {
                message.error(
                  error?.message
                    ? error.message
                    : this.props.t('cms.referral.commission_plan.modal.error.unknown'),
                  7,
                );
              })
              .finally(() => {
                this.setState({ loading: false });
              });
          }
        }
      },
    );
    return true;
  };

  handleUpdateState = (fieldName, fieldValue) => {
    this.setState({
      [fieldName]: fieldValue,
    });
  };

  handleChangeCountry = item => {
    const shouldDisableAmountCalculatingSelector = ['CHN'].includes(item.node.countryCode);
    this.setState({
      partnerCommissionCalculation: item.node.partnerCommissionCalculation,
      currency: item.node.partnerCommissionCalculation.paymentCurrency,
      countryCode: item.node.countryCode,
      ...(shouldDisableAmountCalculatingSelector && {
        calculationType: 'VALUE',
      }),
    });
  };

  render() {
    const { t, type, form } = this.props;
    const { getFieldDecorator } = form;
    const {
      comissionPlanName,
      externalName,
      countryCode,
      calculationType,
      defaultPlan,
      tiers,
      partnerCommissionCalculation,
    } = this.state;

    const shouldDisableAmountCalculatingSelector = countryCode === 'CHN';

    return (
      <div className="commission-plan-editor-container">
        <div className="commission-plan-editor-container__header">
          {t(`cms.referral.commission_plan.modal.${type}_commission_plan.title`)}
          <CloseOutlined
            className="commission-plan-editor-container__close-button"
            onClick={this.props.handleCloseModal}
          />
        </div>
        <div className="commission-plan-editor-container__body">
          <Form className="commission-plan-editor__form">
            <section className="commission-plan-editor__section">
              <h3 className="commission-plan-editor-container__body__title">
                {t('cms.referral.commission_plan.modal.basic_info.title')}
              </h3>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={t('cms.referral.commission_plan.modal.commission_plan_name.label')}
                    colon={false}
                  >
                    {getFieldDecorator('comissionPlanName', {
                      rules: [
                        {
                          required: true,
                          message: t('cms.referral.commission_plan.modal.error.blank'),
                        },
                        {
                          whitespace: true,
                          message: t('cms.referral.commission_plan.modal.error.invalid'),
                        },
                        {
                          max: 255,
                          message: t(
                            'cms.referral.commission_plan.modal.error.commission_plan_name.limit',
                          ),
                        },
                        {
                          validator: (rule, value, callback) => {
                            if (value?.trim() != this.props.plan?.name && value?.trim().length) {
                              this.props.handleCheckDuplicateName(value).then(res => {
                                if (res.checkReferralCommissionPlanNameExists.exists) {
                                  return callback(
                                    t(
                                      'cms.referral.commission_plan.modal.error.commission_plan_name.duplicate',
                                    ),
                                  );
                                } else {
                                  callback();
                                }
                              });
                            } else {
                              callback();
                            }
                          },
                        },
                      ],
                      initialValue: comissionPlanName,
                      validateTrigger: 'onBlur',
                    })(
                      <Input
                        autoComplete="off"
                        placeholder={t(
                          'cms.referral.commission_plan.modal.commission_plan_name.label',
                        )}
                      />,
                    )}
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label={t('cms.referral.commission_plan.modal.external_name.label')}
                    colon={false}
                  >
                    {getFieldDecorator('externalName', {
                      rules: [
                        {
                          required: true,
                          message: t('cms.referral.commission_plan.modal.error.blank'),
                        },
                        {
                          max: 255,
                          message: t(
                            'cms.referral.commission_plan.modal.error.external_name.limit',
                          ),
                        },
                        {
                          whitespace: true,
                          message: t('cms.referral.commission_plan.modal.error.invalid'),
                        },
                      ],
                      initialValue: externalName,
                      validateTrigger: 'onBlur',
                    })(
                      <Input
                        placeholder={t('cms.referral.commission_plan.modal.external_name.label')}
                        autoComplete="off"
                      />,
                    )}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={t('cms.referral.commission_plan.modal.country.label')}
                    colon={false}
                    disabled={type === 'edit'}
                  >
                    {this.props.form.getFieldDecorator('country', {
                      rules: [
                        {
                          required: true,
                          message: t('cms.referral.commission_plan.modal.error.blank'),
                        },
                      ],
                      disabled: type === 'edit',
                      initialValue: countryCode,
                      validateTrigger: 'onBlur',
                    })(
                      <AsyncCountrySelector
                        disabled={type === 'edit'}
                        queryCountrySlugs={['cn', 'in', 'sg', 'id', 'my', 'th']}
                        onSelectItem={this.handleChangeCountry}
                      />,
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t('cms.referral.commission_plan.modal.amount_calculating.label')}
                    colon={false}
                  >
                    <Select
                      disabled={type === 'edit' || shouldDisableAmountCalculatingSelector}
                      value={calculationType}
                      placeholder={t(
                        'cms.referral.commission_plan.modal.amount_calculating.placeholder',
                      )}
                      onChange={v => this.setState({ calculationType: v })}
                    >
                      <Select.Option value="VALUE">
                        {t('cms.referral.commission_plan.modal.amount_calculating.option.fixed')}
                      </Select.Option>
                      <Select.Option value="PERCENTAGE" style={{ whiteSpace: 'normal' }}>
                        {t(
                          'cms.referral.commission_plan.modal.amount_calculating.option.percentage',
                        )}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {partnerCommissionCalculation && (
                <Row gutter={24}>
                  <Col span={12}>
                    <div className="commission-plan-editor__preview-plan-extra-info">
                      <div>
                        <label>
                          {t('cms.referral.commission_plan.modal.payout_currency.label')}:{' '}
                        </label>
                        <span>{partnerCommissionCalculation.paymentCurrency}</span>
                      </div>
                      <div>
                        <label>
                          {t('cms.referral.commission_plan.modal.year_scope_in_row.label')}:{' '}
                        </label>
                        <span>
                          {formatDay(
                            dayFormatType.ABBREVIATION,
                            partnerCommissionCalculation.calculationPeriodStart,
                          )}
                          -
                          {formatDay(
                            dayFormatType.ABBREVIATION,
                            partnerCommissionCalculation.calculationPeriodEnd,
                          )}
                        </span>
                      </div>
                      <div>
                        <label>{t('cms.referral.commission_plan.modal.short_term.label')}: </label>
                        <span>
                          {`＜ ${partnerCommissionCalculation.leaseThresholdValue} ${t(
                            `cms.referral.commission_plan.modal.lease_threshold_unit.${partnerCommissionCalculation.leaseThresholdUnit}`,
                          )}`}
                        </span>
                      </div>
                      <div>
                        <label>{t('cms.referral.commission_plan.modal.long_term.label')}: </label>
                        <span>
                          {`≥ ${partnerCommissionCalculation.leaseThresholdValue} ${t(
                            `cms.referral.commission_plan.modal.lease_threshold_unit.${partnerCommissionCalculation.leaseThresholdUnit}`,
                          )}`}
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={t('cms.referral.commission_plan.modal.set_as_default.label')}
                    colon={false}
                  >
                    {this.props.form.getFieldDecorator('default', {
                      initialValue: defaultPlan,
                      valuePropName: 'checked',
                    })(
                      <Switch
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                        className="commission-plan-editor-container__body__switch"
                      />,
                    )}
                    <p className="commission-plan-editor-container__body__description">
                      {t('cms.referral.commission_plan.modal.set_as_default.description')}
                    </p>
                  </Form.Item>
                </Col>
              </Row>
            </section>

            <section>
              <h3 className="commission-plan-editor-container__body__title">
                {t('cms.referral.commission_plan.modal.commission_tiers.title')}
              </h3>
              <TiersGroup
                tiersValue={tiers}
                selectedCurrency={this.state.currency}
                calculationType={calculationType}
                unit={calculationType === 'PERCENTAGE' ? '%' : this.state.currency}
                onChange={v => {
                  this.handleUpdateState('tiers', v);
                }}
                onRequestValidate={(fieldIndex, validateFieldKey) =>
                  this.handleValidateTier({ fieldIndex, validateFieldKey })
                }
                errInfo={this.state.tiersErrInfo}
              />
            </section>
          </Form>
        </div>

        <div className="commission-plan-editor-container__footer">
          <Button
            ghost
            type="primary"
            size="large"
            className="commission-plan-editor-container__cancel-button"
            onClick={this.props.handleCloseModal}
          >
            {t('cms.referral.commission_plan.modal.btn.cancel')}
          </Button>
          <Popconfirm
            overlayStyle={{ maxWidth: 260 }}
            visible={this.state.defaultPopconfirmVisible}
            onVisibleChange={this.handleBeforeSubmitCheck}
            onConfirm={() => {
              this.handleClickConfirm(true);
            }}
            onCancel={() => {
              this.setState({ defaultPopconfirmVisible: false });
              this.setState({ loading: false });
            }}
            placement="top"
            title={this.state.defaultPopConfirmLabel}
            okText={t('cms.referral.commission_plan.modal.set_default_plan_popover.btn.yes')}
            cancelText={t('cms.referral.commission_plan.modal.set_default_plan_popover.btn.no')}
            arrowPointAtCenter
          >
            <Button
              type="primary"
              size="large"
              className="commission-plan-editor-container__save-button"
              loading={this.state.loading}
            >
              {t('cms.referral.commission_plan.modal.btn.save')}
            </Button>
          </Popconfirm>
        </div>
      </div>
    );
  }
}

CommissionPlanEditor.propTypes = {
  t: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['create', 'edit']),
  plan: PropTypes.object,
  handleCloseModal: PropTypes.func.isRequired,
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
    validateFieldsAndScroll: PropTypes.func.isRequired,
    getFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleCheckDefaultPlan: PropTypes.func.isRequired,
  handleCheckDuplicateName: PropTypes.func.isRequired,
};

CommissionPlanEditor.defaultProps = {
  t: () => {},
  type: 'create',
  plan: {},
  handleCloseModal: () => {},
  form: {
    getFieldDecorator: () => {},
    setFieldsValue: () => {},
    validateFieldsAndScroll: () => {},
    getFieldsValue: () => {},
  },
  handleSubmit: () => {},
  handleCheckDefaultPlan: () => {},
  handleCheckDuplicateName: () => {},
};

export default Form.create({
  name: 'commission_plan_editor_modal',
})(CommissionPlanEditor);
